import {Table} from 'src/design/atoms/Table';
import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {EntityActiveStatusPanel} from 'src/pages/Modules/Clients/shared/EntityActiveStatusPanel';
import {
  InspectionTypeDocumentTitle,
  InspectionTypeViewGeneralMeta,
} from 'src/pages/Modules/Inspections/shared/inspectionTypeHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useDeleteInspectionTypeMutation,
  useGetInspectionTypeEquipmentTypesQuery,
  useGetInspectionTypeQuery,
  useUpdateInspectionTypeActiveStatusMutation,
} from 'src/redux/queries/inspectionType';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2} from 'src/types/FormV2';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const InspectionTypeViewPage = () => {
  const navigate = useAppNavigate();
  const {inspectionTypeId} = useParams();
  const {setAlert} = useAlert();
  const [isDeleting, setIsDeleting] = useState(false);
  const inspectionPerms = useInspectionPermissions();
  useSetDocumentTitle(InspectionTypeDocumentTitle);

  const validId =
    inspectionTypeId !== undefined && !isNaN(+inspectionTypeId)
      ? +inspectionTypeId
      : undefined;

  const {data: viewItem, isLoading} = useGetInspectionTypeQuery(validId!, {
    skip: !validId,
  });

  const {data: coveredEquipment, isLoading: isCoveredEquipmentLoading} =
    useGetInspectionTypeEquipmentTypesQuery(validId!, {
      skip: !validId,
    });

  const [deleteInspectionType] = useDeleteInspectionTypeMutation();
  const [updateActiveStatus, {isLoading: isTogglingActive}] =
    useUpdateInspectionTypeActiveStatusMutation();

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!inspectionTypeId) {
        setAlert('ID not found.', 'error');
        return;
      }

      setIsDeleting(true);

      await deleteInspectionType(+inspectionTypeId).unwrap();

      setAlert('Inspection Type was successfully deleted.', 'success');

      navigate(PathUtility.Inspections('inspection-types'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsDeleting(false);
  };

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  if (!viewItem) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <Alert color="red" title="Not Found">
          Inspection Type not found.
        </Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.Inspections('inspection-types')}
          fallbackLabel="Back to Inspection Types"
          breadcrumbs={[
            {
              label: 'Inspection Types',
              to: PathUtility.Inspections('inspection-types'),
            },
            {label: viewItem.name ?? 'Inspection Type'},
          ]}
        />

        <PageHeader>
          <PageHeader.Title icon={SidebarLinkInspections.icon}>
            {viewItem.name ?? 'Inspection Type'}
          </PageHeader.Title>

          <PageHeader.Badges>
            <PageHeader.Badge
              color={viewItem.active ? 'green' : 'yellow'}
              variant="light">
              {viewItem.active ? 'Active' : 'Inactive'}
            </PageHeader.Badge>
          </PageHeader.Badges>

          <PageHeader.Actions>
            {inspectionPerms.update && (
              <PageHeader.Action
                variant="edit"
                onClick={() =>
                  navigate(PathUtility.InspectionTypesEdit(inspectionTypeId!))
                }>
                Edit
              </PageHeader.Action>
            )}
            {inspectionPerms.delete && (
              <PageHeader.Action
                variant="delete"
                onClick={() => confirmDeleteHandlers.open()}
                loading={isDeleting}>
                Delete
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <FormViewComponentV2
          formData={viewItem as IFormDataV2}
          formMeta={InspectionTypeViewGeneralMeta}
        />

        <SectionCard title="Covered Equipment">
          {isCoveredEquipmentLoading ? (
            <Center py="lg">
              <Loader size="sm" />
            </Center>
          ) : !coveredEquipment || coveredEquipment.length === 0 ? (
            <Text c="dimmed" size="sm" p="md">
              No equipment types are tagged with this inspection type yet.
            </Text>
          ) : (
            <Table data-testid="covered-equipment-table">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Device Count</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {coveredEquipment.map(equipmentType => (
                  <Table.Tr key={equipmentType.id}>
                    <Table.Td>{equipmentType.name}</Table.Td>
                    <Table.Td>{equipmentType.deviceCount}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </SectionCard>

        {inspectionPerms.update && (
          <EntityActiveStatusPanel
            entityLabel="Inspection Type"
            isActive={!!viewItem.active}
            loading={isTogglingActive}
            onToggle={() =>
              updateActiveStatus({
                id: viewItem.id,
                active: !viewItem.active,
              }).unwrap()
            }
          />
        )}
      </Stack>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this inspection type?"
        body="Equipment types already tagged with this inspection type keep the tag. Deactivate it instead if you only want to retire it from new tagging."
      />
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.InspectionTypesView(':inspectionTypeId'),
  element: <InspectionTypeViewPage />,
  module: ModuleEnum.Inspections,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
